/**
 * Zentrale Navigations-Konfiguration — Single Source of Truth.
 * Sidebar (Desktop), Bottom-Bar + „Mehr"-Drawer (Mobil) und die Home-Kacheln
 * speisen sich alle hieraus. Rollen-Filter mit Multi-Role-Support (z.B.
 * "office,techniker") — dieselbe Logik wie ProtectedRoute / api.ts userHasRole().
 */
import type { ZugangBereich, ZugangFachbereich } from '@/types';
import {
  Home,
  Inbox,
  ClipboardCheck,
  Droplets,
  ClipboardList,
  FileText,
  Settings,
  TrendingUp,
  MessageSquareText,
  ListChecks,
  Telescope,
  CalendarCheck,
  Briefcase,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  path: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Schlüssel für die Berechtigungen. Ohne Angabe: für alle sichtbar. */
  bereich?: ZugangBereich;
  /** Sichtbar, wenn MINDESTENS einer dieser Bereiche freigeschaltet ist —
   *  fürs Feedback-Modul, das mehrere Kanäle statt einer einzelnen Kachel hat. */
  bereichAny?: ZugangBereich[];
  /** Wenn gesetzt: nur für diese Rollen sichtbar. Sonst für alle. */
  roles?: string[];
  /**
   * Wenn gesetzt: nur für diese Fachbereiche sichtbar. Der Fachbereich ist
   * etwas anderes als ein `bereich` — er sagt, WOZU jemand gehört, nicht was
   * er sehen darf. Beobachtungen sind Meldungen an die Geschäftsführung; sie
   * hängen deshalb am Fachbereich `gf` und nicht am Bereich `controlling`
   * (den haben auch Projektleitung und Büro).
   */
  nurFachbereich?: ZugangFachbereich[];
  /** Farbklasse für die Home-Kachel. */
  colorClass: string;
}

export const NAV_ITEMS: NavItem[] = [
  { path: '/home', label: 'Start', description: 'Übersicht & Schnellzugriff', icon: Home, colorClass: 'bg-secondary' },
  {
    // Vereint das frühere "Dashboard" (reine Lead-Kennzahlen) als dritten Reiter
    // "Übersicht" in dieser Seite — beide waren de facto Lead-Ansichten und
    // überschnitten sich. bereichAny statt bereich, damit niemand die Kachel
    // verliert, der bisher nur "leads" ODER nur "dashboard" freigeschaltet hatte.
    path: '/leads', bereichAny: ['leads', 'dashboard'], label: 'Leads', description: 'Anfragen, Pipeline & Übersicht',
    icon: Inbox, colorClass: 'bg-secondary',
  },
  { path: '/audit/new', bereich: 'audit', label: 'Vor-Ort-Aufnahme', description: 'Spontan beim Kunden erfassen', icon: ClipboardCheck, colorClass: 'bg-gradient-water' },
  { path: '/leckortung/new', bereich: 'leckortung', label: 'Leckortung', description: 'Ortungsbericht erstellen', icon: Droplets, colorClass: 'bg-gradient-water' },
  { path: '/tagesbericht/new', bereich: 'tagesbericht', label: 'Baustellenbericht', description: 'Stunden gegen das Soll einer Baustelle erfassen', icon: ClipboardList, colorClass: 'bg-gradient-fire' },
  {
    // Hiess vorher "Feedback" — fuer die meisten (v.a. Kundendienst ohne
    // Baustellen-Bezug) IST das ihr Tagesbericht. "Baustellenbericht" direkt
    // davor benennt jetzt explizit den Sonderfall mit Stunden gegen ein Soll.
    path: '/feedback', label: 'Tagesbericht', description: 'Was lief gut, was schlecht — kurz einsprechen',
    icon: MessageSquareText, colorClass: 'bg-gradient-fire',
    bereichAny: ['feedback_kundendienst', 'feedback_projektleitung', 'feedback_buero', 'feedback_gf'],
    // Hier wird bewusst nichts automatisch ausgeblendet -- auch nicht, wenn
    // jemand den Buerobericht hat. Wer den Tagesbericht nicht mehr braucht,
    // bekommt die feedback_*-Haken abgenommen; das ist eine Entscheidung pro
    // Person und gehoert in die Rechtemaske, nicht in eine Regel im Code. Beim
    // Chef waere die Regel falsch gewesen: er nutzt den Tagesbericht taeglich
    // fuer Fuehrungsnotizen und hatte den Buerobericht nur zum Testen an.
  },
  {
    // Buerotaetigkeits-Dokumentation (PC-33/34/35). Einsprechen wie im
    // Tagesbericht, zusaetzlich Zeit, Stueckzahl und was unterbrochen hat.
    // Dauerbetrieb -- die einzige Steuerung ist der Haken in der Matrix.
    path: '/buerobericht', bereich: 'buerobericht', label: 'Bürobericht',
    description: 'Bürotätigkeiten einsprechen — Zeit, Output, Störungen',
    icon: Briefcase, colorClass: 'bg-gradient-fire',
  },
  { path: '/controlling', bereich: 'controlling', label: 'Projektcontrolling', description: 'Soll gegen Ist je Baustelle', icon: TrendingUp, colorClass: 'bg-primary' },
  {
    // Die andere Achse zu "Tagesbericht": nicht was habe ICH eingesprochen,
    // sondern was fällt über alle Leute und Wochen hinweg immer wieder auf.
    // Sprachnotiz-Beobachtungen und Baustellen-Störungen in einer Liste.
    path: '/beobachtungen', label: 'Beobachtungen', description: 'Was sich über Wochen zu Mustern verdichtet',
    icon: Telescope, colorClass: 'bg-primary', nurFachbereich: ['gf'],
  },
  {
    // Kontrollansicht der Geschaeftsfuehrung: wer hat an welchen Werktagen
    // ueberhaupt gemeldet. Zaehlt nur, bewertet nicht.
    path: '/meldungen', label: 'Meldungen', description: 'Wer hat an welchen Tagen berichtet',
    icon: CalendarCheck, colorClass: 'bg-primary', nurFachbereich: ['gf'],
  },
  { path: '/berichte', bereich: 'berichte', label: 'Berichte', description: 'Alle Berichte anzeigen', icon: FileText, colorClass: 'bg-secondary' },
  { path: '/aufgaben', label: 'Aufgaben', description: 'Was ich mir selbst vorgenommen habe', icon: ListChecks, colorClass: 'bg-secondary' },
  { path: '/settings', bereich: 'einstellungen', label: 'Einstellungen', description: 'Regelwerk, Formulare & Sätze je Bereich', icon: Settings, roles: ['geschaeftsfuehrung'], colorClass: 'bg-muted' },
];

/** Rollen-String ("office,techniker") in eine Liste zerlegen (Komma/Pipe/Whitespace, lowercase). */
export function parseRoles(role?: string | null): string[] {
  return (role || '')
    .toLowerCase()
    .split(/[,|\s]+/)
    .map((r) => r.trim())
    .filter(Boolean);
}

/** Nav-Items auf die Rollen des Users filtern. */
export function filterNavByRole<T extends { roles?: string[] }>(items: T[], role?: string | null): T[] {
  const roles = parseRoles(role);
  return items.filter((it) => !it.roles || it.roles.some((r) => roles.includes(r.toLowerCase())));
}

/** Aktiv-Erkennung per erstem Pfad-Segment (/leads/123 → aktiv bei /leads). */
export function isNavActive(pathname: string, itemPath: string): boolean {
  const seg = (p: string) => '/' + (p.split('/')[1] || '');
  return seg(pathname) === seg(itemPath);
}

/**
 * Zusätzlich zur Rollenprüfung: was die Berechtigungsliste erlaubt.
 *
 * Zwei Filter hintereinander statt einem, weil sie verschiedene Fragen
 * beantworten — die Rolle sagt, wofür jemand zuständig ist, die Liste sagt,
 * was die Geschäftsführung ausdrücklich freigegeben hat. Solange die Rechte
 * nicht geladen sind (`geladen === false`), wird nichts weggenommen.
 */
export function filterNavByRechte(
  items: NavItem[],
  rechte: string[],
  geladen: boolean,
  fachbereich?: string | null,
): NavItem[] {
  if (!geladen) return items;
  return items.filter((i) => {
    // `nurFachbereich` ist bewusst NICHT fail-open: ist der Fachbereich nicht
    // bekannt (altes Backend, Fehler beim Laden), bleibt die Kachel weg. Eine
    // Kachel anzubieten, hinter der ein 403 wartet, ist die schlechtere Hälfte
    // des Kompromisses.
    if (i.nurFachbereich && !i.nurFachbereich.includes(fachbereich as never)) return false;
    if (i.bereich) return rechte.includes(i.bereich);
    if (i.bereichAny) return i.bereichAny.some((b) => rechte.includes(b));
    return true;
  });
}

